import { ClientEvent, NotificationCountType, RoomEvent, SyncState } from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useState } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { getAllMessengerRooms } from 'modules/Messenger/components/ConversationsList/utils';

interface UseTotalUnreadCountOptions {
  soundMutedRoomIds?: Set<string>;
}

const useTotalUnreadCount = ({ soundMutedRoomIds }: UseTotalUnreadCountOptions = {}): number => {
  const { matrixClient } = useMatrixContext();
  const [count, setCount] = useState(0);

  const recalculate = useCallback(() => {
    const rooms = getAllMessengerRooms(matrixClient);
    const total = rooms.reduce((acc, room) => {
      if (soundMutedRoomIds?.has(room.roomId)) return acc;
      return acc + (room.getRoomUnreadNotificationCount(NotificationCountType.Total) ?? 0);
    }, 0);
    setCount(total);
  }, [matrixClient, soundMutedRoomIds]);

  useEffect(() => {
    const handleSync = (state: SyncState) => {
      if (state === SyncState.Prepared || state === SyncState.Syncing) recalculate();
    };

    const handleTimeline = (
      _event: unknown,
      _room: unknown,
      toStartOfTimeline?: boolean,
      _removed?: boolean,
      data?: { liveEvent?: boolean }
    ) => {
      if (toStartOfTimeline || !data?.liveEvent) return;
      recalculate();
    };

    matrixClient.on(ClientEvent.Sync, handleSync);
    matrixClient.on(RoomEvent.Receipt, recalculate);
    matrixClient.on(RoomEvent.Timeline, handleTimeline);

    const syncState = matrixClient.getSyncState();
    if (syncState === SyncState.Prepared || syncState === SyncState.Syncing) recalculate();

    return () => {
      matrixClient.off(ClientEvent.Sync, handleSync);
      matrixClient.off(RoomEvent.Receipt, recalculate);
      matrixClient.off(RoomEvent.Timeline, handleTimeline);
    };
  }, [matrixClient, recalculate]);

  return count;
};

export default useTotalUnreadCount;
